import { useCallback } from "react";
import type { SendEmailFormData } from "@/components/shared/formElements/SendEmailForm";
import { toSendEmailTaskParameters } from "@/features/email/utils/send-email-task-parameters";
import {
  STUDENT_TASK_ACTIONS,
  STUDENT_TASK_ENTITY,
} from "@/features/tasks/constants";
import { useCreateTask } from "@/features/tasks/hooks";
import { useTenantCurrencyNumeric } from "@/hooks/shared/useTenantCurrencyNumeric";

export function useStudentTaskSubmit() {
  const currency = useTenantCurrencyNumeric();
  const { createTaskAsync, isPending } = useCreateTask();

  const submitSendEmail = useCallback(
    async (studentIds: number[], data: SendEmailFormData) => {
      await createTaskAsync({
        entityName: STUDENT_TASK_ENTITY,
        actionName: STUDENT_TASK_ACTIONS.sendEmail,
        selectedIds: studentIds,
        executionParameters: toSendEmailTaskParameters(data),
        files: data.attachments,
      });
    },
    [createTaskAsync],
  );

  const submitAddFee = useCallback(
    async (
      studentIds: number[],
      amount: number,
      transactionCode: string,
      note?: string,
    ) => {
      const trimmedNote = note?.trim();
      await createTaskAsync({
        entityName: STUDENT_TASK_ENTITY,
        actionName: STUDENT_TASK_ACTIONS.addFee,
        selectedIds: studentIds,
        executionParameters: {
          Currency: currency,
          Amount: amount,
          TransactionCode: transactionCode,
          ...(trimmedNote ? { Note: trimmedNote } : {}),
        },
      });
    },
    [createTaskAsync, currency],
  );

  const submitDeactivate = useCallback(
    async (studentIds: number[]) => {
      await createTaskAsync({
        entityName: STUDENT_TASK_ENTITY,
        actionName: STUDENT_TASK_ACTIONS.bulkDeactivate,
        selectedIds: studentIds,
      });
    },
    [createTaskAsync],
  );

  const submitSetRecital = useCallback(
    async (studentIds: number[], recital: boolean) => {
      await createTaskAsync({
        entityName: STUDENT_TASK_ENTITY,
        actionName: STUDENT_TASK_ACTIONS.bulkSetRecital,
        selectedIds: studentIds,
        executionParameters: { Recital: recital },
      });
    },
    [createTaskAsync],
  );

  return {
    submitSendEmail,
    submitAddFee,
    submitDeactivate,
    submitSetRecital,
    isPending,
  };
}
